import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowDownLeftIcon,
  ArrowLeftIcon,
  ArrowUpRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HourglassIcon,
  type LucideIcon,
  PenLineIcon,
  ReplyIcon,
  SendIcon,
} from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { EmptyState } from '@/components/empty-state'
import { Favicon } from '@/components/favicon'
import { Panel } from '@/components/panel'
import { ScoreMeter } from '@/components/score-meter'
import { Button } from '@/components/ui/button'
import { bareUrl, formatDate, formatDateTime, formatExact, formatRelative } from '@/lib/format'
import {
  type OutreachMessage,
  type OutreachReply,
  type Prospect,
  type ProspectDetail,
  useOutreachQueue,
  useProspect,
  useReply,
} from '@/lib/outreach'
import { cn } from '@/lib/utils'
import { BACK_LINK, DetailHeader, TableSkeleton, useSiteScope } from '../-section'
import { DraftEditor } from './-draft'
import { DismissMenu, dismissLabel, MoveMenu, ReplyChip, SourceLine, StatusChip } from './-parts'

export const Route = createFileRoute('/_app/$org/$host/outreach/$prospectId')({
  component: ProspectDetailPage,
})

/**
 * One domain, whole: what we saw, what we would say, what came back and how it
 * got where it is.
 *
 * Outside the section's pathless layout for the reason every other detail page
 * is — one click deep, and there is nothing here a stage nav could narrow. The
 * mail is prose and is set as prose; everything the machine produced around it
 * — the domain, the addresses, the positions, the dates — is in the other
 * register, so the two never have to be told apart by reading them.
 */
function ProspectDetailPage() {
  const { org, host } = useSiteScope()
  const { prospectId } = Route.useParams()
  const { data, isPending, error } = useProspect(prospectId)

  const back = (
    <Link to="/$org/$host/outreach" params={{ org, host }} className={BACK_LINK}>
      <ArrowLeftIcon className="size-3" aria-hidden />
      Outreach
    </Link>
  )

  if (error) {
    return (
      <>
        <div className="mb-4">{back}</div>
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="Prospect not found"
            description={`Nothing with that id is on ${host}'s list.`}
            action={
              <Button asChild size="sm" variant="outline">
                <Link to="/$org/$host/outreach" params={{ org, host }}>
                  Back to outreach
                </Link>
              </Button>
            }
          />
        </div>
      </>
    )
  }

  if (isPending || !data) {
    return (
      <>
        <div className="mb-4">{back}</div>
        <TableSkeleton />
      </>
    )
  }

  return <Body detail={data} back={back} />
}

function Body({ detail, back }: { detail: ProspectDetail; back: React.ReactNode }) {
  const { org, host, site } = useSiteScope()
  const navigate = useNavigate()
  const { prospect, messages, replies } = detail
  const open = messages.find(
    (m) => m.status === 'draft' || m.status === 'approved' || m.status === 'queued',
  )
  useSendOutcome(messages, prospect.contactEmail)
  const { data: queue } = useOutreachQueue(site, open?.status === 'approved' ? 'approved' : 'draft')
  const sent = messages.filter((m) => m !== open && m.status !== 'cancelled')
  const dismissable = ['candidate', 'researching', 'ready', 'approved'].includes(prospect.status)
  const movable = ['contacted', 'replied', 'won', 'lost'].includes(prospect.status)

  const items = open ? (queue?.items ?? []) : []
  const index = items.findIndex((item) => item.prospect.id === prospect.id)
  const previous = index > 0 ? items[index - 1] : undefined
  const next = index >= 0 ? items[index + 1] : undefined

  function go(to: string | undefined) {
    if (to)
      navigate({ to: '/$org/$host/outreach/$prospectId', params: { org, host, prospectId: to } })
    else navigate({ to: '/$org/$host/outreach', params: { org, host } })
  }

  return (
    <>
      <DetailHeader
        back={back}
        title={prospect.domain}
        actions={
          index >= 0 ? (
            <div className="flex items-center gap-1.5">
              <span className="mr-1 font-mono text-[11px] text-muted-foreground tabular">
                {formatExact(index + 1)} / {formatExact(items.length)}
              </span>
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                aria-label="Previous"
                disabled={!previous}
                onClick={() => go(previous?.prospect.id)}
              >
                <ChevronLeftIcon />
              </Button>
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                aria-label="Next"
                disabled={!next}
                onClick={() => go(next?.prospect.id)}
              >
                <ChevronRightIcon />
              </Button>
            </div>
          ) : dismissable ? (
            <DismissMenu siteId={site?.id} prospectId={prospect.id} domain={prospect.domain} />
          ) : movable ? (
            <MoveMenu siteId={site?.id} prospect={prospect} />
          ) : undefined
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Favicon host={prospect.domain} size={14} className="shrink-0 rounded-[3px]" />
        <StatusChip status={prospect.status} closeReason={prospect.closeReason} />
        {prospect.dismissReason && (
          <span className="text-[13px] text-muted-foreground">
            {dismissLabel(prospect.dismissReason)}
            {prospect.dismissedBy ? ` · ${prospect.dismissedBy}` : ''}
          </span>
        )}
        {!open && prospect.contactEmail && (
          <a
            href={`mailto:${prospect.contactEmail}`}
            className="ml-auto font-mono text-[12px] text-link underline-offset-4 hover:underline"
          >
            {prospect.contactEmail}
          </a>
        )}
      </div>

      {prospect.firstContactedAt && <ThreadState prospect={prospect} />}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          {open && (
            <DraftEditor
              key={open.id}
              message={open}
              prospect={prospect}
              siteId={site?.id}
              onDone={() => go((next ?? previous)?.prospect.id)}
            />
          )}
          {(sent.length > 0 || replies.length > 0) && (
            <Thread messages={sent} replies={replies} to={prospect.contactEmail} />
          )}
          {!open && prospect.firstContactedAt && prospect.contactEmail && (
            <ReplyBox prospectId={prospect.id} to={prospect.contactEmail} siteId={site?.id} />
          )}
          {!open && sent.length === 0 && replies.length === 0 && (
            <div className="rounded-lg border border-border bg-card">
              <EmptyState
                title="Nothing written"
                description={`No mail has been written to ${prospect.domain}.`}
              />
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Facts prospect={prospect} />
        </div>
      </div>
    </>
  )
}

/** Says how a send in flight ended, once, when the row stops being `queued`. */
function useSendOutcome(messages: OutreachMessage[], to: string | null) {
  const watching = useRef<string | null>(null)

  useEffect(() => {
    const queued = messages.find((m) => m.status === 'queued')
    if (queued) {
      watching.current = queued.id
      return
    }
    const id = watching.current
    if (!id) return
    watching.current = null

    const landed = messages.find((m) => m.id === id)
    if (landed?.status === 'sent') toast.success(to ? `Sent to ${to}` : 'Sent')
    else toast.error(landed?.error ?? 'Not sent')
  }, [messages, to])
}

/** Their page, ours, and the one sentence that joins them. */
function Facts({ prospect }: { prospect: Prospect }) {
  return (
    <Panel title="The page">
      <div className="space-y-4 text-[13px]">
        <div className="space-y-1.5">
          <a
            href={prospect.pageUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="block text-[13px] underline-offset-2 hover:underline"
          >
            {prospect.pageTitle ?? bareUrl(prospect.pageUrl)}
          </a>
          <SourceLine prospect={prospect} />
        </div>

        {prospect.hook && (
          <blockquote className="border-l-2 border-border pl-3 text-[12.5px] leading-[1.6] text-muted-foreground">
            {prospect.hook}
          </blockquote>
        )}

        {prospect.reason && (
          <p className="text-[12.5px] leading-[1.6] text-muted-foreground">{prospect.reason}</p>
        )}

        <dl className="space-y-3">
          {prospect.targetUrl && (
            <Fact
              label="Offering"
              value={<span className="truncate text-[12px]">{bareUrl(prospect.targetUrl)}</span>}
            />
          )}
          {prospect.suggestedTarget && (
            <Fact
              label="Found for"
              value={<span className="text-[12px]">{prospect.suggestedTarget}</span>}
            />
          )}
          {prospect.rank !== null && (
            <Fact label="Authority" value={<ScoreMeter kind="authority" value={prospect.rank} />} />
          )}
          {prospect.research.lastPublished && (
            <Fact
              label="Updated"
              value={<span>{formatDate(prospect.research.lastPublished)}</span>}
            />
          )}
          {prospect.research.outboundLinks !== null && (
            <Fact
              label="Links out"
              value={<span>{formatExact(prospect.research.outboundLinks)}</span>}
            />
          )}
          {prospect.research.affiliateLinks > 0 && (
            <Fact
              label="Affiliate links"
              value={<span>{formatExact(prospect.research.affiliateLinks)}</span>}
            />
          )}
          {prospect.createdAt && (
            <Fact label="Found" value={<span>{formatDate(prospect.createdAt)}</span>} />
          )}
        </dl>
      </div>
    </Panel>
  )
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="mono-label shrink-0 text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 items-baseline justify-end gap-1.5 font-mono tabular">{value}</dd>
    </div>
  )
}

/**
 * Where the thread stands, read before the thread itself: whose move it is,
 * and the two times that say how long it has been.
 */
function ThreadState({ prospect }: { prospect: Prospect }) {
  const { waiting, firstContactedAt, lastContactedAt, lastReplyAt } = prospect
  const theirs = lastReplyAt !== null && (!lastContactedAt || lastReplyAt > lastContactedAt)
  const last = theirs ? lastReplyAt : lastContactedAt
  const yours = waiting?.on === 'you'

  return (
    <div
      className={cn(
        'mb-4 grid gap-4 rounded-lg border bg-card px-4 py-3 sm:grid-cols-3',
        yours ? 'border-foreground/40' : 'border-border',
      )}
    >
      <Stat label="Waiting on">
        {waiting ? (
          <>
            <StatValue
              icon={yours ? (waiting.why === 'reply' ? ReplyIcon : PenLineIcon) : HourglassIcon}
              strong={yours}
            >
              {yours ? 'You' : 'Them'}
            </StatValue>
            {waiting.on === 'you' && (
              <StatNote>
                {waiting.why === 'reply' ? 'They replied' : 'Follow-up to approve'}
              </StatNote>
            )}
          </>
        ) : (
          <StatValue icon={CheckIcon}>Nobody</StatValue>
        )}
      </Stat>

      {firstContactedAt && (
        <Stat label="First contact">
          <StatValue icon={SendIcon}>{formatRelative(firstContactedAt)}</StatValue>
          <StatNote>{formatDateTime(firstContactedAt)}</StatNote>
        </Stat>
      )}

      {last && (
        <Stat label="Last message">
          <StatValue icon={theirs ? ArrowDownLeftIcon : ArrowUpRightIcon}>
            {formatRelative(last)}
          </StatValue>
          <StatNote>
            {theirs ? 'From them' : 'From you'} · {formatDateTime(last)}
          </StatNote>
        </Stat>
      )}
    </div>
  )
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="mono-label text-muted-foreground">{label}</p>
      {children}
    </div>
  )
}

function StatValue({
  icon: Icon,
  strong = false,
  children,
}: {
  icon: LucideIcon
  strong?: boolean
  children: React.ReactNode
}) {
  return (
    <p className={cn('flex items-center gap-2 text-[15px] leading-6', strong && 'font-semibold')}>
      <Icon className={cn('size-4 shrink-0', !strong && 'text-muted-foreground')} aria-hidden />
      {children}
    </p>
  )
}

function StatNote({ children }: { children: React.ReactNode }) {
  return <p className="truncate font-mono text-[11px] text-muted-foreground tabular">{children}</p>
}

type ThreadEntry =
  | { kind: 'sent'; at: string | null; message: OutreachMessage }
  | { kind: 'reply'; at: string | null; reply: OutreachReply }

/** What went out and what came back, in the order it happened. */
function Thread({
  messages,
  replies,
  to,
}: {
  messages: OutreachMessage[]
  replies: OutreachReply[]
  to: string | null
}) {
  const entries: ThreadEntry[] = [
    ...messages.map((message) => ({
      kind: 'sent' as const,
      at: message.sentAt ?? message.approvedAt ?? message.createdAt,
      message,
    })),
    ...replies.map((reply) => ({ kind: 'reply' as const, at: reply.receivedAt, reply })),
  ].sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''))

  return (
    <Panel title="Thread" meta={formatExact(entries.length)} flush>
      <ul className="divide-y divide-border">
        {entries.map((entry) => {
          const mine = entry.kind === 'sent'
          const subject = mine ? entry.message.subject : entry.reply.subject
          const body = mine ? entry.message.body : entry.reply.body
          return (
            <li key={mine ? entry.message.id : entry.reply.id} className="space-y-2 px-4 py-3.5">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                {mine ? (
                  <ArrowUpRightIcon className="size-3.5 text-muted-foreground" aria-hidden />
                ) : (
                  <ArrowDownLeftIcon className="size-3.5" aria-hidden />
                )}
                <span
                  className={cn('text-[12.5px]', mine ? 'text-muted-foreground' : 'font-medium')}
                >
                  {mine ? `You to ${to ?? 'them'}` : entry.reply.fromAddress}
                </span>
                {mine && entry.message.status !== 'sent' && (
                  <span className="mono-label rounded-sm bg-secondary px-1 py-px text-muted-foreground">
                    {entry.message.status}
                  </span>
                )}
                {!mine && <ReplyChip classification={entry.reply.classification} />}
                {entry.at && (
                  <span
                    className="ml-auto font-mono text-[11px] text-muted-foreground tabular"
                    title={formatDateTime(entry.at)}
                  >
                    {formatRelative(entry.at)}
                  </span>
                )}
              </div>

              {subject && <p className="text-[13.5px] leading-snug font-medium">{subject}</p>}
              {body && (
                <p className="max-w-[70ch] text-[13px] leading-relaxed whitespace-pre-wrap text-muted-foreground">
                  {body}
                </p>
              )}
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

/** Answering in the thread: the person typing it is the approval, so it sends. */
function ReplyBox({
  prospectId,
  to,
  siteId,
}: {
  prospectId: string
  to: string
  siteId: string | undefined
}) {
  const [body, setBody] = useState('')
  const reply = useReply(siteId)
  const id = useId()
  const sendable = body.trim() !== '' && !reply.isPending

  async function send() {
    if (!sendable) return
    try {
      await reply.mutateAsync({ prospectId, body: body.trim() })
      setBody('')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Not sent')
    }
  }

  return (
    <form
      className="overflow-hidden rounded-lg border border-border bg-card"
      onSubmit={(event) => {
        event.preventDefault()
        void send()
      }}
    >
      <label className="block" htmlFor={id}>
        <span className="sr-only">Reply to {to}</span>
        <textarea
          id={id}
          value={body}
          rows={Math.min(20, Math.max(5, body.split('\n').length + 2))}
          placeholder={`Reply to ${to}`}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault()
              void send()
            }
          }}
          className="block w-full resize-y bg-transparent px-4 py-3.5 text-[13.5px] leading-[1.7] outline-none placeholder:text-muted-foreground"
        />
      </label>
      <footer className="flex items-center justify-end border-t border-border bg-secondary/20 px-4 py-2.5">
        <Button type="submit" size="sm" disabled={!sendable}>
          <SendIcon />
          Send
        </Button>
      </footer>
    </form>
  )
}
